import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import useApi from '../../../utils/AxiosWrapper';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../../utils/UserContext';
import SignupFooter from '../SignupFooter';
import { Entity, PartnerSignupStep } from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { Input } from 'antd';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';
import {
  createPartnerContactInfo,
  updatePartnerSignupStep,
} from './PartnerSignupApis';

interface PartnerManagementContactProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
}
export type PersonalFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyPosition: string;
};

function PartnerManagementContact({
  currStep,
  selectedRole,
  setStep,
}: PartnerManagementContactProps) {
  const { callProtectedApi } = useApi();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PersonalFormData>();
  const { userRecord, userId } = useUser();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();
  /* eslint-disable @typescript-eslint/no-unused-vars */

  const [pageLoading, setPageLoading] = useState(false);
  const onSubmit: SubmitHandler<PersonalFormData> = (data) => {
    safeSessionSetItem('managementContactInfo', JSON.stringify(data));
    createPartnerContactInfo(
      data,
      userId ?? '',
      callProtectedApi,
      handleSetLoading,
      setStep,
      PartnerSignupStep.PartnerContract
    );
    updatePartnerSignupStep(
      userId ?? '',
      callProtectedApi,
      handleSetLoading,
      setStep,
      PartnerSignupStep.PartnerContract
    );
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  useEffect(() => {
    // Retrieve form data from session storage
    const savedData = safeSessionGetItem('managementContactInfo');
    if (savedData) {
      const parsedData: PersonalFormData = JSON.parse(savedData);
      // Set form data using setValue
      for (const key in parsedData) {
        if (parsedData.hasOwnProperty(key)) {
          setValue(
            key as keyof PersonalFormData,
            parsedData[key as keyof PersonalFormData]
          );
        }
      }
    }
  }, [setValue]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle`}
          >
            {!loading ? (
              <>
                <div className={`'w-[60%] mb-6 flex flex-row items-start`}>
                  <div className="flex flex-col items-center">
                    <h2 className="text-start text-[30px] font-bold">
                      Additional Management Contact
                    </h2>
                    <p className="text-gray-500">
                      Please add a contact person responsible for public
                      relations.
                    </p>
                  </div>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[60%] space-y-4`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First Name
                        </label>
                        <Controller
                          name="firstName"
                          control={control}
                          rules={{ required: 'First name is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="firstName"
                              placeholder="John"
                              status={errors.firstName ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="lastName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last Name
                        </label>
                        <Controller
                          name="lastName"
                          control={control}
                          rules={{ required: 'Last name is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="lastName"
                              placeholder="Doe"
                              status={errors.lastName ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <Controller
                          name="email"
                          control={control}
                          rules={{
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Invalid email address',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="email"
                              id="email"
                              placeholder="johndoe@example.com"
                              status={errors.email ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="phoneNumber"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone Number
                        </label>
                        <Controller
                          name="phoneNumber"
                          control={control}
                          rules={{
                            required: 'Phone number is required',
                            pattern: {
                              value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/,
                              message:
                                'Phone number must be in the format +1 (XXX) XXX-XXXX',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="tel"
                              id="phoneNumber"
                              placeholder="+1 (555) 123-4567"
                              status={errors.phoneNumber ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                              onChange={(e) => {
                                let value = e.target.value;

                                // Remove the +1 prefix if present to avoid extracting it as a digit
                                if (value.startsWith('+1')) {
                                  value = value.substring(2);
                                }

                                // Extract only digits
                                let digits = value.replace(/\D/g, '');

                                // Limit to 10 digits
                                if (digits.length > 10) {
                                  digits = digits.substring(0, 10);
                                }

                                // Format as +1 (XXX) XXX-XXXX
                                let formatted = '+1';
                                if (digits.length > 0) {
                                  formatted += ` (${digits.substring(0, 3)}`;
                                  if (digits.length > 3) {
                                    formatted += `) ${digits.substring(3, 6)}`;
                                    if (digits.length > 6) {
                                      formatted += `-${digits.substring(6)}`;
                                    }
                                  }
                                }

                                field.onChange(formatted);
                              }}
                            />
                          )}
                        />
                        {errors.phoneNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.phoneNumber.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="companyPosition"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Position in the Company
                      </label>
                      <Controller
                        name="companyPosition"
                        control={control}
                        rules={{ required: 'Company Position is Required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="companyPosition"
                            placeholder="Owner"
                            status={errors.companyPosition ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.companyPosition && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.companyPosition.message}
                        </p>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      onBackClick={() => setStep('partner_personal_info')}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={pageLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle`}
          >
            {!loading ? (
              <>
                <div className={`'w-[60%] mb-6 flex flex-row items-start`}>
                  <div className="flex flex-col items-center">
                    <h2 className="text-start text-[30px] font-bold">
                      Additional Management Contact
                    </h2>
                    <p className="text-gray-500">
                      Please add a contact person responsible for public
                      relations.
                    </p>
                  </div>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[60%] space-y-4`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First Name
                        </label>
                        <Controller
                          name="firstName"
                          control={control}
                          rules={{ required: 'First name is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="firstName"
                              placeholder="John"
                              status={errors.firstName ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="lastName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last Name
                        </label>
                        <Controller
                          name="lastName"
                          control={control}
                          rules={{ required: 'Last name is required' }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="lastName"
                              placeholder="Doe"
                              status={errors.lastName ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <Controller
                          name="email"
                          control={control}
                          rules={{
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Invalid email address',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="email"
                              id="email"
                              placeholder="johndoe@example.com"
                              status={errors.email ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="phoneNumber"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone Number
                        </label>
                        <Controller
                          name="phoneNumber"
                          control={control}
                          rules={{
                            required: 'Phone number is required',
                            pattern: {
                              value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/,
                              message:
                                'Phone number must be in the format +1 (XXX) XXX-XXXX',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="tel"
                              id="phoneNumber"
                              placeholder="+1 (555) 123-4567"
                              status={errors.phoneNumber ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                              onChange={(e) => {
                                let value = e.target.value;

                                // Remove the +1 prefix if present to avoid extracting it as a digit
                                if (value.startsWith('+1')) {
                                  value = value.substring(2);
                                }

                                // Extract only digits
                                let digits = value.replace(/\D/g, '');

                                // Limit to 10 digits
                                if (digits.length > 10) {
                                  digits = digits.substring(0, 10);
                                }

                                // Format as +1 (XXX) XXX-XXXX
                                let formatted = '+1';
                                if (digits.length > 0) {
                                  formatted += ` (${digits.substring(0, 3)}`;
                                  if (digits.length > 3) {
                                    formatted += `) ${digits.substring(3, 6)}`;
                                    if (digits.length > 6) {
                                      formatted += `-${digits.substring(6)}`;
                                    }
                                  }
                                }

                                field.onChange(formatted);
                              }}
                            />
                          )}
                        />
                        {errors.phoneNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.phoneNumber.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="companyPosition"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Position in the Company
                      </label>
                      <Controller
                        name="companyPosition"
                        control={control}
                        rules={{ required: 'Company Position is Required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="companyPosition"
                            placeholder="Owner"
                            status={errors.companyPosition ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.companyPosition && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.companyPosition.message}
                        </p>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      onBackClick={() => setStep('partner_personal_info')}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={pageLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle`}
          >
            {!loading ? (
              <>
                <div className={`'w-full mb-6 flex flex-row items-start`}>
                  <div className="flex flex-col items-center">
                    <h2 className="text-center text-[30px] font-bold">
                      Additional Management Contact
                    </h2>
                    <p className="text-center text-gray-500">
                      Please add a contact person responsible for public
                      relations.
                    </p>
                  </div>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[95%] space-y-4`}
                  >
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        First Name
                      </label>
                      <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: 'First name is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="firstName"
                            placeholder="John"
                            status={errors.firstName ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last Name
                      </label>
                      <Controller
                        name="lastName"
                        control={control}
                        rules={{ required: 'Last name is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="lastName"
                            placeholder="Doe"
                            status={errors.lastName ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.lastName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          required: 'Email is required',
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Invalid email address',
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="email"
                            id="email"
                            placeholder="johndoe@example.com"
                            status={errors.email ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Phone Number
                      </label>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        rules={{
                          required: 'Phone number is required',
                          pattern: {
                            value: /^\+1[0-9]{10}$/,
                            message:
                              'Phone number must be in the format +1XXXXXXXXXX',
                          },
                          onChange: (e) => {
                            // Automatically format the input to start with +1
                            if (!e.target.value.startsWith('+1')) {
                              e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                            }
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="tel"
                            id="phoneNumber"
                            placeholder="1234567890"
                            status={errors.phoneNumber ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.phoneNumber && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.phoneNumber.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="companyPosition"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Position in the Company
                      </label>
                      <Controller
                        name="companyPosition"
                        control={control}
                        rules={{ required: 'Company Position is Required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="companyPosition"
                            placeholder="Owner"
                            status={errors.companyPosition ? 'error' : ''}
                            className="mt-1 h-[38px]"
                            style={{ borderRadius: 0 }}
                          />
                        )}
                      />
                      {errors.companyPosition && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.companyPosition.message}
                        </p>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      onBackClick={() => setStep('partner_personal_info')}
                      isMobile={true}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={pageLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PartnerManagementContact;
